import {
  ActionIcon,
  Box,
  Button,
  Col,
  Container,
  Divider,
  Flex,
  Grid,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Radio,
  Switch,
  Text,
  TextInput,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { openConfirmModal } from "@mantine/modals";
import {
  IconCheck,
  IconDownload,
  IconEdit,
  IconPlus,
  IconPrinter,
  IconTrash,
  IconX,
  IconEye,
} from "@tabler/icons";
import FloatingMenu from "components/FloatingMenu";
import { PrintModal } from "components/PrintModal";
import produce from "immer";
import { MantineReactTable } from "mantine-react-table";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import {
  getBodyContent,
  getCsvContent,
  getFields,
  getFilterPayload,
  getHeaders,
  getTotal,
} from "services/filterHelperFunctions";
import { api_filter } from "services/master.service";

import { coupons_api } from "./coupons.service";
import AppBreadcrumb from "../../../components/AppBreadcrumb";
import { showErrorToast, showSuccessToast } from "../../../utilities/Toast";
import { invoice_api } from "pages/transactions/invoice/invoice.service";
import { counters_api } from "../counter/counter.service";

export default function Coupons() {
  const [head, setHead] = useState([]);
  const [body, setBody] = useState([]);
  const [foot, setFoot] = useState([]);
  const [csv, setCsv] = useState([]);
  const [isCsv, setIsCsv] = useState(false);
  const theme = useMantineTheme();
  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  // START : States
  const [coupons, setCoupons] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [filename, setFilename] = useState("file");
  const [modalOpened, setModalOpened] = useState(false);
  const [viewReturnModalOpened, setViewReturnModalOpened] = useState(false);
  const [returnModalDetails, setReturnModalDetails] = useState({});
  const [editingId, setEditingId] = useState(null);

  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [productOfferMap, setProductOfferMap] = useState({});
  const [productQtyMap, setProductQtyMap] = useState({});
  const [search, setSearch] = useState("");
  const [offerModalOpened, setOfferModalOpened] = useState(false);
  const [viewOfferModalOpened, setViewOfferModalOpened] = useState(false);
  const [viewOfferDetails, setViewOfferDetails] = useState(null);

  const handleViewOffer = (row) => {
    setViewOfferDetails(row);
    setViewOfferModalOpened(true);
  };
  // END : States
  const [columnFilters, setColumnFilters] = useState([]);

  const loadProducts = async () => {
    try {
      const countersRes = await counters_api.get();
      const counters = countersRes?.data || [];
      const saleableCounter = counters.find(c => c.saleable === true);
      
      if (saleableCounter) {
        const res = await invoice_api.get_products(saleableCounter.id, 100, 1);
        if (res?.success) {
          setProducts(res.data || []);
        }
      } else {
        showErrorToast({
          title: "Error",
          message: "There is no saleable counter",
        });
      }
    } catch (err) {
      console.error("Error loading products for coupon offers:", err);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const closeOfferModal = () => {
    setOfferModalOpened(false);
    setSelectedIds([]);
    setProductOfferMap({});
    setProductQtyMap({});
    offerForm.reset();
    setIsEditing(false);
    setEditingId(null);
  };

  const handleOfferEdit = (row) => {
    setIsEditing(true);
    setEditingId(row.id);
    
    offerForm.setValues({
      coupon_code: row.coupon_code,
      cutoff_amount: row.cutoff_amount,
      coupon_qty: row.coupon_qty || 0,
    });
    
    const ids = [];
    const offerMap = {};
    const qtyMap = {};
    
    if (row.offer_products) {
      row.offer_products.forEach(op => {
        const stockId = op.counter_stock || op.counter_stock_id;
        if (stockId) {
          const idStr = stockId.toString();
          ids.push(idStr);
          offerMap[idStr] = op.offer_price;
          qtyMap[idStr] = op.quantity;
        }
      });
    }
    
    setSelectedIds(ids);
    setProductOfferMap(offerMap);
    setProductQtyMap(qtyMap);
    setOfferModalOpened(true);
  };

  const offerForm = useForm({
    validateInputOnBlur: true,
    initialValues: {
      coupon_code: "",
      coupon_qty: 999999,
      cutoff_amount: "",
    },
    validate: {
      coupon_code: value => (value ? null : "Coupon code is required"),
      cutoff_amount: value => (value >= 0 ? null : "Cutoff amount must be valid"),
    },
  });

  const selectedProducts = useMemo(() => {
    return selectedIds
      .map(id => products.find(p => p.id.toString() === id))
      .filter(Boolean);
  }, [selectedIds, products]);

  const filteredProducts = useMemo(() => {
    return products.filter(p =>
      (p.title || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [products, search]);

  const dropdownData = filteredProducts.map(p => ({
    value: p.id.toString(),
    label: p.title,
    mrp: p.mrp,
    selling_price: p.selling_price,
    stock_qty: p.stock_qty,
  }));

  const mergedDropdownData = useMemo(() => {
    const selectedOptions = selectedProducts.map(p => ({
      value: p.id.toString(),
      label: p.title,
      mrp: p.mrp,
      selling_price: p.selling_price,
      stock_qty: p.stock_qty,
    }));

    const map = new Map();
    selectedOptions.forEach(opt => map.set(opt.value, opt));
    dropdownData.forEach(opt => {
      if (!map.has(opt.value)) {
        map.set(opt.value, opt);
      }
    });

    return Array.from(map.values());
  }, [dropdownData, selectedProducts]);

  const handleOfferFormSubmit = async (values) => {
    if (selectedIds.length === 0) {
      showErrorToast({ title: "Error", message: "Please select at least one product" });
      return;
    }

    // Verify all selected products have an offer price set
    for (let id of selectedIds) {
      if (productOfferMap[id] === undefined || productOfferMap[id] === "") {
        showErrorToast({ title: "Error", message: "Please set offer price for all selected products" });
        return;
      }
    }

    // Verify quantity does not exceed stock
    for (let id of selectedIds) {
      const prod = products.find(p => p.id.toString() === id);
      const qty = productQtyMap[id] !== undefined ? Number(productQtyMap[id]) : 1;
      if (qty > (prod?.stock_qty || 0)) {
        showErrorToast({ title: "Error", message: `Quantity for ${prod?.title} cannot exceed available stock (${prod?.stock_qty})` });
        return;
      }
    }

    const payload = {
      coupon_code: values.coupon_code,
      coupon_qty: Number(values.coupon_qty),
      cutoff_amount: Number(values.cutoff_amount),
      products: selectedProducts.map(p => ({
        product_id: p.product,
        counter_stock_id: p.id,
        price: Number(productOfferMap[p.id]),
        quantity: productQtyMap[p.id] !== undefined ? Number(productQtyMap[p.id]) : 1,
        mrp: p.mrp,
        variant: p.isVarient && p.variants?.length ? p.variants[0] + p.varientUnit : "1pkt"
      }))
    };

    if (isEditing) {
      await coupons_api.edit(editingId, payload).then(res => {
        if (res.success) {
          showSuccessToast({ title: "Success", message: "Offer Coupon edited successfully." });
          closeOfferModal();
          coupons_data.refetch();
        } else {
          showErrorToast({ title: "Error", message: res.message });
        }
      });
    } else {
      await coupons_api.addOfferCoupon(payload).then(res => {
        if (res.success) {
          showSuccessToast({ title: "Success", message: "Offer Coupon added successfully." });
          closeOfferModal();
          coupons_data.refetch();
        } else {
          showErrorToast({ title: "Error", message: res.message });
        }
      });
    }
  };

  const handleEdit = values => {
    setIsEditing(true);
    console.log(values);
    returnForm.setValues(values);
    setEditingId(values.id);
    setModalOpened(true);
  };

  const returnForm = useForm({
    validateInputOnBlur: true,
    shouldUnregister: false,
    initialValues: {
      coupon_code: "",
      coupon_qty: "",
      coupon_type: "flat",
      value: "",
      cutoff_amount: "",
    },
    validate: {
      coupon_code: value => (value ? null : "Coupon code is required"),
      coupon_type: value => (value ? null : "Coupon qty is required"),
      value: value => (value ? null : "Value is required"),
      cutoff_amount: value => (value >= 0 ? null : "Cutoff amount must be valid"),
    },
  });

  const openDeleteConfirmation = id => {
    openConfirmModal({
      title: "Please confirm delete coupon",
      children: (
        <Text size="sm">
          Are you sure you want to delete this coupon ? This action can't be undone.
        </Text>
      ),
      labels: { confirm: "Delete Coupon", cancel: "Cancel" },
      onCancel: () => console.log("Cancel"),
      onConfirm: () => deleteCoupon(id),
      confirmProps: { color: "red" },
    });
  };

  const deleteCoupon = async id => {
    await coupons_api.delete(id).then(res => {
      if (res.success) {
        showSuccessToast({ title: "Success", message: res.message });
        coupons_data.refetch();
      } else {
        showErrorToast({ title: "Error", message: res.message });
      }
    });
  };

  const changeStatus = (id, status) => {
    setCoupons(
      produce(draft => {
        let product = draft.find(e => e.id === id);
        product.active = status;
      })
    );
  };

  const handleStatusChange = async (productId, status) => {
    await coupons_api
      .changeStatus({
        product_id: productId,
      })
      .then(res => {
        if (res.success) {
          changeStatus(productId, status);
        } else {
          showErrorToast({ title: "Error", message: res.message });
        }
      });
  };

  // const coupons_data = useQuery("coupons", coupons_api.get, {
  //   onSuccess: data => setCoupons(data.data),
  // });

  const coupons_data = useQuery({
    queryKey: ["coupons", columnFilters], // Include filters in queryKey
    queryFn: async () => {
      if (columnFilters.length > 0) {
        return printFilter(false); // Fetch filtered data when table filters are applied
      } else {
        return coupons_api.get();
      }
    },
    onSuccess: data => setCoupons(data.data),
  });


  const handleFormSubmit = async values => {
    console.log("Coupon values", values);

    await coupons_api.add(values).then(res => {
      console.log(res);
      if (res.success) {
        showSuccessToast({
          title: "Success",
          message: "Coupon added successfully.",
        });
        setModalOpened(false);
        coupons_data.refetch();
      } else {
        showSuccessToast({
          title: "Error",
          message: res.message,
        });
      }
    });
  };

  const handleCouponEdit = async values => {
    console.log("Coupon values", values);

    await coupons_api
      .edit(editingId, {
        ...values,
      })
      .then(res => {
        console.log(res);
        if (res.success) {
          showSuccessToast({
            title: "Success",
            message: "Coupon edited successfully.",
          });
          setModalOpened(false);
          coupons_data.refetch();
        } else {
          showSuccessToast({
            title: "Error",
            message: res.message,
          });
        }
      });
  };

  const columns = useMemo(
    () => [
      {
        header: "Coupon Code",
        accessorKey: "coupon_code",
        size: "auto",
      },
      {
        header: "Type",
        accessorKey: "coupon_type",
        size: "auto",
        Cell: ({ cell, row }) => <Text tt={"capitalize"}>{`${cell.getValue()}`}</Text>,
      },
      {
        header: "Value",
        accessorKey: "value",
        size: "auto",
        filterVariant: "range",
        Cell: ({ cell, row }) => (
          <Text>
            {" "}
            {row.original.coupon_type === "flat"
              ? `${cell.getValue()} %`
              : `Rs ${cell.getValue()}`}{" "}
          </Text>
        ),
      },
      {
        header: "Total Coupons",
        accessorKey: "coupon_qty",
        size: "auto",
        filterVariant: "range",
      },
      {
        header: "Cutoff Amount",
        accessorKey: "cutoff_amount",
        size: "auto",
        Cell: ({ cell }) => <Text>Rs {cell.getValue()}</Text>,
      },

      {
        header: "Status",
        accessorKey: "active",
        size: "auto",
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ["true", "false"],
        },
        Cell: ({ cell, row }) => (
          <Switch
            checked={cell.getValue()}
            thumbIcon={
              cell.getValue() ? (
                <IconCheck
                  size="0.8rem"
                  color={theme.colors.teal[theme.fn.primaryShade()]}
                  stroke={3}
                />
              ) : (
                <IconX size="0.8rem" color={theme.colors.red[theme.fn.primaryShade()]} stroke={3} />
              )
            }
          />
        ),
      },
      // {
      //   header: 'Coupons Remaining',
      //   accessorKey: 'remaining_coupons',
      //   size: 50,
      // },
    ],
    []
  );

  const offerColumns = useMemo(
    () => [
      {
        header: "Coupon Code",
        accessorKey: "coupon_code",
        size: "auto",
      },
      {
        header: "Cutoff Amount",
        accessorKey: "cutoff_amount",
        size: "auto",
        Cell: ({ cell }) => <Text>Rs {cell.getValue()}</Text>,
      },
      {
        header: "Offer Product Count",
        accessorKey: "offer_products",
        size: "auto",
        Cell: ({ cell }) => {
          const prods = cell.getValue() || [];
          return <Text>{prods.length}</Text>;
        },
      },
      {
        header: "Status",
        accessorKey: "active",
        size: "auto",
        Cell: ({ cell, row }) => (
          <Switch
            checked={cell.getValue()}
            thumbIcon={
              cell.getValue() ? (
                <IconCheck
                  size="0.8rem"
                  color={theme.colors.teal[theme.fn.primaryShade()]}
                  stroke={3}
                />
              ) : (
                <IconX size="0.8rem" color={theme.colors.red[theme.fn.primaryShade()]} stroke={3} />
              )
            }
          />
        ),
      },
    ],
    [theme]
  );

  const standardCoupons = useMemo(() => {
    return (coupons || []).filter(c => !c.offer_products || c.offer_products.length === 0);
  }, [coupons]);

  const offerCoupons = useMemo(() => {
    return (coupons || []).filter(c => c.offer_products && c.offer_products.length > 0);
  }, [coupons]);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    //console.log(head);
    if (body.length !== 0 && head.length !== 0) {
      if (!isCsv) {
        handlePrint();
        setBody([]);
        setHead([]);
      }
    }
    setIsCsv(false);
  }, [body, head]);

  // const get_text = html => {
  //   let div = document.createElement("div");
  //   div.innerHTML = html;
  //   let text = div.textContent || div.innerText || "";
  //   return text;
  // };

  // const get_sum = removeAction => {
  //   const datatablebody = document
  //     .querySelector(".datatable-wrapper")
  //     ?.querySelector("tbody")
  //     ?.querySelectorAll("tr");
  //   let column_total = [];
  //   datatablebody?.forEach(e => {
  //     let row_total = [];
  //     const d = e?.querySelectorAll("td");
  //     d?.forEach((tag, i) => {
  //       if (!removeAction || d.length !== i + 1) {
  //         row_total.push(parseFloat(get_text(tag?.innerHTML).replace(/[^0-9.]/g, "")));
  //       }
  //     });
  //     column_total.push(row_total);
  //   });

  //   let total = [];
  //   column_total.map((e, i) => {
  //     e.map((h, index) => {
  //       if (i === 0) {
  //         total.push(h);
  //       } else {
  //         total[index] = h + total[index];
  //       }
  //     });
  //   });

  //   let nan_count = 0;
  //   total.map((e, i) => {
  //     if (isNaN(e)) {
  //       nan_count += 1;
  //     }
  //   });
  //   if (nan_count === total.length) {
  //     setFoot([]);
  //   } else {
  //     setFoot(total);
  //   }

  //   return total;
  // };

  // const print = removeAction => {
  //   var head_content = [];
  //   var body_content = [];
  //   const datatable = document
  //     .querySelector(".datatable-wrapper")
  //     ?.querySelector("thead")
  //     ?.querySelectorAll("th");
  //   datatable !== undefined &&
  //     datatable?.forEach((e, i) => {
  //       if (!removeAction || datatable.length - 1 > i + 1) {
  //         head_content.push(e?.querySelector(".mantine-TableHeadCell-Content-Wrapper")?.innerText);
  //       }
  //     });

  //   const datatablebody = document
  //     .querySelector(".datatable-wrapper")
  //     ?.querySelector("tbody")
  //     ?.querySelectorAll("tr");
  //   datatablebody?.forEach(e => {
  //     let row = [];
  //     const d = e?.querySelectorAll("td");
  //     d?.forEach((tag, i) => {
  //       if (!removeAction || d.length - 1 > i + 1) {
  //         row.push(get_text(tag?.innerHTML));
  //       }
  //     });
  //     body_content.push(row);
  //   });

  //   setHead(head_content);
  //   setBody(body_content);
  //   let csv_data = [];
  //   csv_data.push(head_content);
  //   body_content.map((e, i) => {
  //     csv_data.push(e);
  //   });
  //   const cols = [2, 3];
  //   let sum = [];
  //   get_sum(removeAction).map((e, i) => {
  //     if (isNaN(e) || !cols?.includes(i, 0)) {
  //       sum.push("");
  //     } else {
  //       sum.push(e);
  //     }
  //   });
  //   csv_data.push(sum);
  //   setCsv(csv_data);
  // };

  // const printFilter = async () => {
  //   const model = "Coupon";
  //   const filter = getFilterPayload(columns, columnFilters);
  //   const fields = getFields(columns);

  //   await api_filter(model, filter, fields)
  //     .then(res => {
  //       if (res.success) {
  //         console.log(res.data);
  //         const head_content = getHeaders(columns);
  //         const body_content = getBodyContent(res.data, fields);
  //         const csv_content = getCsvContent(head_content, body_content, ["COUPONS"]);
  //         const columnTotalIndex = [2, 3];
  //         const foot_content = getTotal(body_content, columnTotalIndex);
  //         setFilename(
  //           "COUPONS" + "_" + "WareHouse" + "_" + new Date().toLocaleDateString() + ".csv"
  //         );
  //         setCsv(csv_content);
  //         setHead(head_content);
  //         setBody(body_content);
  //         setFoot(foot_content);
  //       } else {
  //         showErrorToast({ title: "Error", message: res.message });
  //       }
  //     })
  //     .catch(e => {
  //       showErrorToast({ title: "Error", message: e.message });
  //     });
  // };

  const printFilter = async (forCsv = true) => {
    const model = "Coupon";
    const filterPayload = getFilterPayload(columns, columnFilters);
    const fields = getFields(columns);

    try {
      const res = await api_filter(model, filterPayload, fields);
      if (res.success) {
        let body_content = getBodyContent(res.data, fields);

        if (forCsv) {
          // **CSV Export**
          const head_content = getHeaders(columns);
          const csv_content = getCsvContent(head_content, body_content, ["COUPONS"]);
          const columnTotalIndex = [2, 3]; // Totals for selected columns
          const foot_content = getTotal(body_content, columnTotalIndex);

          setFilename(`COUPONS_WareHouse_${new Date().toLocaleDateString()}.csv`);
          setCsv(csv_content);
          setHead(head_content);
          setBody(body_content);
          setFoot(foot_content);
        } else {
          // **Table Filtering**
          return { data: res.data };
        }
      } else {
        showErrorToast({ title: "Error", message: res.message });
        return { data: [] };
      }
    } catch (e) {
      showErrorToast({ title: "Error", message: e.message });
      return { data: [] };
    }
  };


  const csvRef = useRef();
  useEffect(() => {
    if (csv.length && isCsv) {
      csvRef.current.link.click();
      setCsv([]);
      setIsCsv(false);
    }
  }, [csv]);

  return (
    <>
      <CSVLink
        style={{ display: "none" }}
        asyncOnClick={true}
        data={csv}
        filename={filename}
        ref={csvRef}
      ></CSVLink>
      {isMobileScreen ? (
        <></>
      ) : (
        <Container fluid>
          <Box p={25}>
            <Flex justify={"space-between"} align="end">
              <Box>
                <Text size={"lg"} fw={600} mb={10}>
                  Promotion Coupons
                </Text>
                <AppBreadcrumb />
              </Box>
              <Box>
                <Flex align={"center"} justify={"center"} gap={10}>
                  <Button
                    onClick={() => {
                      setIsCsv(true);
                      printFilter();
                    }}
                    leftIcon={<IconDownload />}
                  >
                    CSV
                  </Button>
                  {/* <Button
                    ml={10}
                    leftIcon={<IconPrinter />}
                    onClick={() => {
                      print(true);
                    }}
                  >
                    Print
					</Button> */}
                  <Button
                    leftIcon={<IconPrinter />}
                    onClick={() => {
                      printFilter();
                    }}
                  >
                    Print
                  </Button>
                  <Button
                    onClick={() => {
                      setOfferModalOpened(true);
                    }}
                    mr={10}
                  >
                    <IconPlus style={{ width: "20px" }} />
                    <Text ml={10}> Add Offer Product </Text>
                  </Button>
                  <Button
                    onClick={() => {
                      setModalOpened(true);
                    }}
                  >
                    <IconPlus style={{ width: "20px" }} />
                    <Text ml={10}> Add Coupon </Text>
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </Box>
        </Container>
      )}
      {/* STARTS :: Main content */}

      <Container fluid mt={10} mb={30}>
        {/* Add Product Form */}

        {/* Add Product Form */}

        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              manualFiltering={true} //turn off client-side filtering
              onColumnFiltersChange={setColumnFilters} //hoist internal columnFilters state to your state
              state={{ columnFilters, isLoading: coupons_data.isLoading || coupons_data.isRefetching }} //pass in your own managed columnFilters state
              mantineTableProps={{ className: "datatable" }}
              enableRowActions
              positionActionsColumn="last"
              renderTopToolbarCustomActions={({ row }) => (
                <Box px={isMobileScreen ? 5 : 24} py={10}>
                  <Text
                    fw={600}
                    tt="uppercase"
                    size={"sm"}
                    sx={theme => ({
                      letterSpacing: 1,
                      color: theme.colors.brand[7],
                    })}
                  >
                    Coupons
                  </Text>
                </Box>
              )}
              renderRowActions={({ row }) => {
                return (
                  <Flex>
                    <Tooltip label="Edit Coupon">
                      <ActionIcon
                        sx={theme => ({ color: theme.colors.brand[7] })}
                        ml={10}
                        onClick={() => {
                          handleEdit(row.original);
                        }}
                      >
                        <IconEdit style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>

                    <Tooltip label="Delete Coupon">
                      <ActionIcon
                        sx={theme => ({ color: theme.colors.red[6] })}
                        ml={10}
                        onClick={() => {
                          openDeleteConfirmation(row.original.id);
                        }}
                      >
                        <IconTrash style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>
                  </Flex>
                );
              }}
              initialState={{ density: "xs" }}
              isLoading={coupons_data.isLoading || coupons_data.isRefetching}
              columns={columns}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              data={standardCoupons || []}
              enableStickyHeader
              enableGlobalFilter={false} //turn off a feature
            />
          </Box>
        </Box>

        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }} mt={40}>
          <Box className="datatable-wrapper">
            <MantineReactTable
              manualFiltering={true}
              onColumnFiltersChange={setColumnFilters}
              state={{ columnFilters, isLoading: coupons_data.isLoading || coupons_data.isRefetching }}
              mantineTableProps={{ className: "datatable" }}
              enableRowActions
              positionActionsColumn="last"
              renderTopToolbarCustomActions={({ row }) => (
                <Box px={isMobileScreen ? 5 : 24} py={10}>
                  <Text
                    fw={600}
                    tt="uppercase"
                    size={"sm"}
                    sx={theme => ({
                      letterSpacing: 1,
                      color: theme.colors.brand[7],
                    })}
                  >
                    Offer Product Coupons
                  </Text>
                </Box>
              )}
              renderRowActions={({ row }) => {
                return (
                  <Flex>
                    <Tooltip label="View Products">
                      <ActionIcon
                        sx={theme => ({ color: theme.colors.blue[6] })}
                        ml={10}
                        onClick={() => {
                          handleViewOffer(row.original);
                        }}
                      >
                        <IconEye style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>

                    <Tooltip label="Edit Coupon">
                      <ActionIcon
                        sx={theme => ({ color: theme.colors.brand[7] })}
                        ml={10}
                        onClick={() => {
                          handleOfferEdit(row.original);
                        }}
                      >
                        <IconEdit style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>

                    <Tooltip label="Delete Coupon">
                      <ActionIcon
                        sx={theme => ({ color: theme.colors.red[6] })}
                        ml={10}
                        onClick={() => {
                          openDeleteConfirmation(row.original.id);
                        }}
                      >
                        <IconTrash style={{ width: 20 }} />
                      </ActionIcon>
                    </Tooltip>
                  </Flex>
                );
              }}
              initialState={{ density: "xs" }}
              isLoading={coupons_data.isLoading || coupons_data.isRefetching}
              columns={offerColumns}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              data={offerCoupons || []}
              enableStickyHeader
              enableGlobalFilter={false}
            />
          </Box>
        </Box>
      </Container>

      {/* ENDS :: Main content */}

      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setIsEditing(false);
        }}
        title={
          <Box px={10}>
            <Text fw={600}>{isEditing ? "Edit coupon" : "Add coupon"}</Text>
          </Box>
        }
        size={"800px"}
        centered
      >
        <Box px={10}>
          <Divider mb={20} />
          <Box>
            <form
              onSubmit={returnForm.onSubmit(values => {
                !isEditing ? handleFormSubmit(values) : handleCouponEdit(values);
              })}
              onError={err => {
                console.log(err);
              }}
            >
              <Grid>
                <Col sm={6}>
                  <TextInput
                    label="Coupon Code"
                    placeholder="Enter coupon code"
                    {...returnForm.getInputProps("coupon_code")}
                  />
                </Col>
                <Col sm={6}>
                  <NumberInput
                    type="number"
                    label="Total Coupons"
                    placeholder="Enter Total Coupons"
                    {...returnForm.getInputProps("coupon_qty")}
                  />
                </Col>
                <Col sm={6}>
                  <NumberInput
                    type="number"
                    label="Cutoff Amount"
                    placeholder="Minimum bill amount"
                    {...returnForm.getInputProps("cutoff_amount")}
                  />
                </Col>

                <Col sm={6}>
                  <Radio.Group
                    defaultValue="amount"
                    label="Select Discount Type"
                    {...returnForm.getInputProps("coupon_type")}
                  >
                    <Group mt="xs">
                      {/* <Radio value="flat" label="Flat" /> */}
                      <Radio value="amount" label="Amount" />
                    </Group>
                  </Radio.Group>
                </Col>
                {returnForm.values.coupon_type === "flat" ? (
                  <Col sm={6}>
                    <NumberInput
                      type="number"
                      label="Flat Percentage"
                      placeholder="Enter Flat Percentage"
                      max={100}
                      {...returnForm.getInputProps("value")}
                    />
                  </Col>
                ) : (
                  <Col sm={6}>
                    <NumberInput
                      type="number"
                      label="Amount"
                      placeholder="Enter coupon amount"
                      {...returnForm.getInputProps("value")}
                    />
                  </Col>
                )}

                <Col span={12} my={10}>
                  <Flex h={"100%"} direction={"column"} justify="end" align={"center"}>
                    <Button w={"100%"} type="submit">
                      {isEditing ? "Edit Coupon" : "Add Coupon"}
                    </Button>
                  </Flex>
                </Col>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <Modal
        opened={offerModalOpened}
        onClose={closeOfferModal}
        title={
          <Box px={10}>
            <Text fw={600}>{isEditing ? "Edit Offer Product Coupon" : "Add Offer Product Coupon"}</Text>
          </Box>
        }
        size={"850px"}
        centered
      >
        <Box px={10}>
          <Divider mb={20} />
          <Box>
            <form onSubmit={offerForm.onSubmit(handleOfferFormSubmit)}>
              <Grid>
                <Col sm={6}>
                  <TextInput
                    label="Coupon Code"
                    placeholder="Enter coupon code"
                    {...offerForm.getInputProps("coupon_code")}
                  />
                </Col>
                <Col sm={6}>
                  <NumberInput
                    label="Cutoff Amount"
                    placeholder="Minimum bill amount (e.g. 500)"
                    min={0}
                    {...offerForm.getInputProps("cutoff_amount")}
                  />
                </Col>
                <Col sm={12}>
                  <MultiSelect
                    label="Select Offer Products"
                    placeholder="Type product name..."
                    searchable
                    clearable
                    data={mergedDropdownData}
                    value={selectedIds}
                    searchValue={search}
                    onSearchChange={setSearch}
                    onChange={(values) => {
                      setSelectedIds(values);
                      setProductOfferMap(prev => {
                        const updated = { ...prev };
                        Object.keys(updated).forEach(key => {
                          if (!values.includes(key.toString())) {
                            delete updated[key];
                          }
                        });
                        return updated;
                      });
                      setProductQtyMap(prev => {
                        const updated = { ...prev };
                        Object.keys(updated).forEach(key => {
                          if (!values.includes(key.toString())) {
                            delete updated[key];
                          }
                        });
                        return updated;
                      });
                    }}
                    maxDropdownHeight={150}
                    nothingFound="No products found in saleable counter"
                    itemComponent={({ label, mrp, selling_price, stock_qty, ...others }) => (
                      <Group {...others} position="apart" noWrap>
                        <Text size="sm">{label}</Text>
                        <Text size="xs" color="dimmed">
                          MRP ₹{mrp} | SP ₹{selling_price} | Stock {stock_qty}
                        </Text>
                      </Group>
                    )}
                  />
                </Col>

                {selectedProducts.length > 0 && (
                  <Col span={12} mt={20}>
                    <Text fw={600} mb={10}>Selected Products & Offer Pricing</Text>
                    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 4, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                            <th style={{ padding: 10, textAlign: "left" }}>Product</th>
                            <th style={{ padding: 10, textAlign: "right" }}>MRP</th>
                            <th style={{ padding: 10, textAlign: "right" }}>Selling Price</th>
                            <th style={{ padding: 10, textAlign: "center", width: 120 }}>Quantity</th>
                            <th style={{ padding: 10, textAlign: "center", width: 150 }}>Offer Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProducts.map(p => (
                            <tr key={p.id} style={{ borderBottom: "1px solid #e0e0e0" }}>
                              <td style={{ padding: 10 }}>{p.title}</td>
                              <td style={{ padding: 10, textAlign: "right" }}>₹{p.mrp}</td>
                              <td style={{ padding: 10, textAlign: "right" }}>₹{p.selling_price}</td>
                              <td style={{ padding: 10, textAlign: "center" }}>
                                <NumberInput
                                  value={productQtyMap[p.id] || 1}
                                  onChange={(val) => setProductQtyMap(prev => ({ ...prev, [p.id]: val || 1 }))}
                                  min={1}
                                  max={p.stock_qty}
                                  size="xs"
                                />
                              </td>
                              <td style={{ padding: 10, textAlign: "center" }}>
                                <NumberInput
                                  value={productOfferMap[p.id] !== undefined ? productOfferMap[p.id] : ""}
                                  onChange={(val) => setProductOfferMap(prev => ({ ...prev, [p.id]: val !== undefined ? val : "" }))}
                                  placeholder="Offer SP"
                                  min={0}
                                  precision={2}
                                  size="xs"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </Col>
                )}

                <Col span={12} my={20}>
                  <Flex h={"100%"} direction={"column"} justify="end" align={"center"}>
                    <Button w={"100%"} type="submit">
                      {isEditing ? "Edit Offer Coupon" : "Create Offer Coupon"}
                    </Button>
                  </Flex>
                </Col>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>

      <Modal
        opened={viewOfferModalOpened}
        onClose={() => {
          setViewOfferModalOpened(false);
          setViewOfferDetails(null);
        }}
        title={
          <Box px={10}>
            <Text fw={600}>Offer Products List - {viewOfferDetails?.coupon_code}</Text>
          </Box>
        }
        size={"800px"}
        centered
      >
        <Box px={10}>
          <Divider mb={20} />
          {viewOfferDetails && viewOfferDetails.offer_products && (
            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 4, overflow: "hidden" }} mb={20}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8f9fa", borderBottom: "1px solid #e0e0e0" }}>
                    <th style={{ padding: "10px 15px", textAlign: "left" }}>Product</th>
                    <th style={{ padding: "10px 15px", textAlign: "center" }}>Variant</th>
                    <th style={{ padding: "10px 15px", textAlign: "center" }}>Quantity</th>
                    <th style={{ padding: "10px 15px", textAlign: "right" }}>Offer Price</th>
                    <th style={{ padding: "10px 15px", textAlign: "right" }}>Selling Price</th>
                    <th style={{ padding: "10px 15px", textAlign: "right" }}>MRP</th>
                  </tr>
                </thead>
                <tbody>
                  {viewOfferDetails.offer_products.map((op, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <td style={{ padding: "10px 15px", textAlign: "left" }}>{op.product_title}</td>
                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{op.varient || "1pkt"}</td>
                      <td style={{ padding: "10px 15px", textAlign: "center" }}>{op.quantity}</td>
                      <td style={{ padding: "10px 15px", textAlign: "right" }}>₹{op.offer_price}</td>
                      <td style={{ padding: "10px 15px", textAlign: "right" }}>₹{op.selling_price || "0"}</td>
                      <td style={{ padding: "10px 15px", textAlign: "right" }}>₹{op.original_price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Box>
      </Modal>

      <div style={{ display: "none" }}>
        <PrintModal
          cols={[2, 3]}
          title="Coupons"
          head={head}
          body={body}
          foots={foot}
          ref={componentRef}
        />
      </div>

      {isMobileScreen ? (
        <>
          <FloatingMenu
            m={5}
            left
            size={50}
            onClick={() => {
              setIsCsv(true);
              printFilter();
            }}
          >
            <IconDownload color="white" />
          </FloatingMenu>
          <FloatingMenu
            m={5}
            ml={60}
            left
            size={50}
            onClick={() => {
              printFilter();
            }}
          >
            <IconPrinter color="white" />
          </FloatingMenu>
          <FloatingMenu
            m={5}
            right
            size={50}
            onClick={() => {
              setModalOpened(true);
            }}
          >
            <IconPlus color="white" />
          </FloatingMenu>
        </>
      ) : (
        <></>
      )}
    </>
  );
}
